import { useCallback, useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TempoElement } from "@/utils/tempoElement";
import { EditTextChange } from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useTextEditStore } from "./textEditStore";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import {
  findElementByDomUniquePath,
  getCodebaseIdFromNode,
  getDomUniquePathFromNode,
  getNavNodeForDomElement,
} from "@/components/Canvas/utils/utils";
import { NavTreeNode } from "@/utils/types";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { toast } from "sonner";

interface TextEditState {
  domUniquePathEditing: string;
  originalText: string;
  element: HTMLElement;
}

export const useTextEdit = (
  storyboardId: string,
  iframeDocRef?: React.MutableRefObject<Document | null>,
) => {
  const [editingState, setEditingState] = useState<TextEditState | null>(null);

  const canEditText = useCallback((elementKey: string): boolean => {
    const tempoElement = TempoElement.fromKey(elementKey);
    const treeElement =
      useCodebaseStore.getState().treeElementLookup[tempoElement.codebaseId];

    if (!treeElement) {
      return false;
    }

    // Check if element has staticTextContents property
    return !!treeElement.staticTextContents;
  }, []);

  const startTextEdit = useCallback(
    (element: HTMLElement) => {
      // Element must have a codebase ID
      const codebaseId = getCodebaseIdFromNode(element);
      if (!codebaseId) {
        toast.error(`Can't find codebase ID for element`);
        return;
      }

      useIframePoolStore
        .getState()
        .setIgnoreUpdatesForStoryboards(storyboardId);

      // Store original text
      const originalText = element.textContent || "";

      // Make element editable
      element.setAttribute("contenteditable", "plaintext-only");
      element.style.cursor = "text";
      element.style.outline = "none";
      element.style.border = "none";

      // Focus and select all text
      element.focus();

      // Get the selection from the iframe's window
      const iframeWindow = element.ownerDocument.defaultView;
      const selection = iframeWindow?.getSelection();
      const range = element.ownerDocument.createRange();
      range.selectNodeContents(element);
      selection?.removeAllRanges();
      selection?.addRange(range);

      setEditingState({
        domUniquePathEditing: codebaseId,
        originalText,
        element,
      });
    },
    [storyboardId],
  );

  const endTextEdit = useCallback(() => {
    if (!editingState) {
      return;
    }

    useIframePoolStore
      .getState()
      .unsetIgnoreUpdatesForStoryboards(storyboardId);

    const { element, originalText } = editingState;
    const newText = element.textContent || "";

    // Remove contenteditable and reset styles
    element.removeAttribute("contenteditable");
    element.style.cursor = "";
    element.style.outline = "";
    element.style.border = "";

    // Clear selection from the iframe's window
    const iframeWindow = element.ownerDocument.defaultView;
    const selection = iframeWindow?.getSelection();
    if (selection) {
      selection.removeAllRanges();
      // Also collapse any existing selection to ensure it's fully cleared
      selection.collapse(null);
    }

    // Remove focus from the element
    element.blur();

    // Only create change if text actually changed
    if (newText !== originalText) {
      const navNode = getNavNodeForDomElement(element, storyboardId);

      if (navNode?.tempoElement?.codebaseId) {
        useDesignCommandManager.getState().executeCommand(
          new EditTextChange({
            codebaseIdToEditText: navNode?.tempoElement?.codebaseId,
            newText,
            oldText: originalText,
          }),
        );
      } else {
        toast.error("Could not find element to edit");
      }
    }

    setEditingState(null);
  }, [editingState, storyboardId]);

  const startTextEditForDomUniquePath = useCallback(
    (domUniquePath?: string) => {
      if (!domUniquePath) {
        console.error(
          "[useTextEdit] No dom unique path available to find element",
        );
        return;
      }

      if (!iframeDocRef?.current) {
        console.error("[useTextEdit] No iframe ref available to find element");
        return;
      }

      const iframeDoc = iframeDocRef.current;

      // Use the utility function to find the element
      const element = findElementByDomUniquePath(iframeDoc, domUniquePath);

      if (!element) {
        console.error(
          `[useTextEdit] Element not found at domUniquePath: ${domUniquePath}`,
        );
        return;
      }

      // Already editing text for this dom unique path, skip
      if (editingState?.domUniquePathEditing === domUniquePath) {
        return;
      }

      // If editing for a different dom unique path, end the current edit
      if (editingState?.domUniquePathEditing !== domUniquePath) {
        endTextEdit();
      }

      // Start text edit on the found element
      startTextEdit(element);

      setTimeout(() => {
        element.focus();
      }, 100);
    },
    [
      editingState?.domUniquePathEditing,
      endTextEdit,
      iframeDocRef,
      startTextEdit,
    ],
  );

  const handleBlur = useCallback(() => {
    endTextEdit();
  }, [endTextEdit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Cancel editing and restore original text
        if (editingState) {
          editingState.element.textContent = editingState.originalText;

          // Clear any selection before ending edit
          const iframeWindow = editingState.element.ownerDocument.defaultView;
          const selection = iframeWindow?.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.collapse(null);
          }

          endTextEdit();
        }
      } else if (e.key === "Enter" && !e.shiftKey) {
        // Finish editing on Enter (without shift for multiline)
        e.preventDefault();
        endTextEdit();
      }
    },
    [editingState, endTextEdit],
  );

  const updateElementKeyBeingEdited = useCallback((elementKey: string) => {
    setEditingState((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        elementKey,
      };
    });
  }, []);

  // Register this hook's ref with the store
  useEffect(() => {
    if (storyboardId && iframeDocRef) {
      const ref = {
        startTextEditForDomUniquePath,
      };

      useTextEditStore.getState().registerTextEditRef(storyboardId, ref);

      return () => {
        useTextEditStore.getState().unregisterTextEditRef(storyboardId);
      };
    }
  }, [
    storyboardId,
    iframeDocRef,
    startTextEdit,
    startTextEditForDomUniquePath,
    endTextEdit,
    editingState,
  ]);

  return {
    canEditText,
    startTextEdit,
    startTextEditForDomUniquePath,
    endTextEdit,
    handleBlur,
    handleKeyDown,
    updateElementKeyBeingEdited,
    isEditing: !!editingState,
    domUniquePathEditing: editingState?.domUniquePathEditing,
  };
};
